import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 0 0',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  select: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 8px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#4338ca',
    backgroundColor: '#eef2ff',
    borderRadius: '9999px',
    letterSpacing: '0.025em',
  },
  svgContainer: {
    width: '100%',
    lineHeight: 0,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    backgroundColor: '#1f2937',
    color: '#ffffff',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    lineHeight: '1.5',
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    whiteSpace: 'nowrap',
  },
  tooltipLabel: {
    color: '#9ca3af',
    marginRight: '6px',
  },
  tooltipValue: {
    fontWeight: 600,
  },
  oocBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 700,
    marginTop: '4px',
  },
  empty: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  initialServiceVisit: 'Initial Service Visit',
  oneTime: 'One-Time',
  mosquitoTickControl: 'Mosquito & Tick Control',
};

export default function EWMAChart({ data }) {
  const svgRef = useRef(null);
  const [selectedField, setSelectedField] = useState('');
  const [tooltip, setTooltip] = useState(null);

  const fieldKeys = useMemo(() => {
    if (!data || !data.fields) return [];
    return Object.keys(data.fields);
  }, [data]);

  useEffect(() => {
    if (fieldKeys.length > 0 && !fieldKeys.includes(selectedField)) {
      setSelectedField(fieldKeys[0]);
    }
  }, [fieldKeys, selectedField]);

  const fieldData = useMemo(() => {
    if (!data || !data.fields || !selectedField) return null;
    return data.fields[selectedField];
  }, [data, selectedField]);

  const dates = useMemo(() => {
    if (!data || !data.dates) return [];
    return data.dates;
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || !fieldData || !dates.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 350;
    const margin = { top: 24, right: 24, bottom: 44, left: 56 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const { ewma, ucl, lcl, target } = fieldData;
    const n = dates.length;

    const parsedDates = dates.map(d => new Date(d));

    const allValues = [...ewma, ...ucl, ...lcl, target];
    const yMin = d3.min(allValues);
    const yMax = d3.max(allValues);
    const yPadding = (yMax - yMin) * 0.1 || 1;

    const xScale = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerWidth]);

    const yScale = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice()
      .range([innerHeight, 0]);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Shaded band between UCL and LCL
    const bandArea = d3.area()
      .x((_, i) => xScale(parsedDates[i]))
      .y0((_, i) => yScale(lcl[i]))
      .y1((_, i) => yScale(ucl[i]))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(ewma)
      .attr('d', bandArea)
      .attr('fill', '#f3f4f6')
      .attr('stroke', 'none');

    // Out-of-control regions highlighted with red background
    for (let i = 0; i < n; i++) {
      if (ewma[i] > ucl[i] || ewma[i] < lcl[i]) {
        const x0 = i === 0 ? xScale(parsedDates[0]) : xScale(parsedDates[i]) - (xScale(parsedDates[i]) - xScale(parsedDates[Math.max(0, i - 1)])) / 2;
        const x1 = i === n - 1 ? xScale(parsedDates[n - 1]) : xScale(parsedDates[i]) + (xScale(parsedDates[Math.min(n - 1, i + 1)]) - xScale(parsedDates[i])) / 2;
        g.append('rect')
          .attr('x', x0)
          .attr('y', 0)
          .attr('width', Math.max(0, x1 - x0))
          .attr('height', innerHeight)
          .attr('fill', '#fef2f2')
          .attr('opacity', 0.7);
      }
    }

    // Gridlines
    const yTicks = yScale.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Target / centerline (dashed gray)
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(target))
      .attr('y2', yScale(target))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4');

    // Target label
    g.append('text')
      .attr('x', innerWidth + 4)
      .attr('y', yScale(target))
      .attr('dy', '0.35em')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .text('CL');

    // UCL line (dashed red)
    const uclLine = d3.line()
      .x((_, i) => xScale(parsedDates[i]))
      .y(d => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(ucl)
      .attr('d', uclLine)
      .attr('fill', 'none')
      .attr('stroke', '#ef4444')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '5,3');

    // LCL line (dashed red)
    const lclLine = d3.line()
      .x((_, i) => xScale(parsedDates[i]))
      .y(d => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(lcl)
      .attr('d', lclLine)
      .attr('fill', 'none')
      .attr('stroke', '#ef4444')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '5,3');

    // UCL / LCL labels
    g.append('text')
      .attr('x', innerWidth + 4)
      .attr('y', yScale(ucl[n - 1]))
      .attr('dy', '0.35em')
      .attr('font-size', '10px')
      .attr('fill', '#ef4444')
      .text('UCL');

    g.append('text')
      .attr('x', innerWidth + 4)
      .attr('y', yScale(lcl[n - 1]))
      .attr('dy', '0.35em')
      .attr('font-size', '10px')
      .attr('fill', '#ef4444')
      .text('LCL');

    // EWMA line (thick blue)
    const ewmaLine = d3.line()
      .x((_, i) => xScale(parsedDates[i]))
      .y(d => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(ewma)
      .attr('d', ewmaLine)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2.5);

    // Out-of-control points highlighted
    for (let i = 0; i < n; i++) {
      const ooc = ewma[i] > ucl[i] || ewma[i] < lcl[i];
      g.append('circle')
        .attr('cx', xScale(parsedDates[i]))
        .attr('cy', yScale(ewma[i]))
        .attr('r', ooc ? 5 : 3)
        .attr('fill', ooc ? '#ef4444' : '#2563eb')
        .attr('stroke', '#ffffff')
        .attr('stroke-width', ooc ? 2 : 1.5);
    }

    // X-axis
    const xAxis = d3.axisBottom(xScale)
      .ticks(Math.min(n, 8))
      .tickFormat(d3.timeFormat('%b %d'));

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .call(g => g.select('.domain').attr('stroke', '#d1d5db'))
      .call(g => g.selectAll('.tick line').attr('stroke', '#d1d5db'))
      .call(g => g.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '11px'));

    // Y-axis
    const yAxis = d3.axisLeft(yScale)
      .ticks(6)
      .tickFormat(d => `$${d.toFixed(2)}`);

    g.append('g')
      .call(yAxis)
      .call(g => g.select('.domain').attr('stroke', '#d1d5db'))
      .call(g => g.selectAll('.tick line').attr('stroke', '#d1d5db'))
      .call(g => g.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '11px'));

    // Invisible overlay for hover
    const bisect = d3.bisector(d => d).left;

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .attr('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const x0 = xScale.invert(mx);
        const idx = bisect(parsedDates, x0, 1);
        const d0 = parsedDates[idx - 1];
        const d1 = parsedDates[idx];
        const i = (!d1 || (x0 - d0 < d1 - x0)) ? idx - 1 : idx;
        if (i < 0 || i >= n) return;

        const ooc = ewma[i] > ucl[i] || ewma[i] < lcl[i];

        setTooltip({
          x: event.clientX + 16,
          y: event.clientY - 12,
          date: dates[i],
          ewmaVal: ewma[i],
          uclVal: ucl[i],
          lclVal: lcl[i],
          ooc,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

  }, [fieldData, dates, selectedField]);

  if (!data || !data.fields || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <h3 style={styles.title}>EWMA Control Chart</h3>
          <p style={styles.subtitle}>Exponentially weighted moving average for shift detection</p>
        </div>
        <div style={styles.empty}>No data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h3 style={styles.title}>EWMA Control Chart</h3>
        <p style={styles.subtitle}>Exponentially weighted moving average for shift detection</p>
      </div>

      <div style={styles.controls}>
        <select
          style={styles.select}
          value={selectedField}
          onChange={e => setSelectedField(e.target.value)}
        >
          {fieldKeys.map(key => (
            <option key={key} value={key}>
              {FIELD_LABELS[key] || key}
            </option>
          ))}
        </select>
        {fieldData && (
          <span style={styles.badge}>
            {'\u03BB'} = {fieldData.lambda}
          </span>
        )}
      </div>

      <div style={styles.svgContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 350"
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: 'auto' }}
        />
      </div>

      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div>
            <span style={styles.tooltipLabel}>Date:</span>
            <span style={styles.tooltipValue}>{tooltip.date}</span>
          </div>
          <div>
            <span style={styles.tooltipLabel}>EWMA:</span>
            <span style={styles.tooltipValue}>${tooltip.ewmaVal.toFixed(2)}</span>
          </div>
          <div>
            <span style={styles.tooltipLabel}>UCL:</span>
            <span style={styles.tooltipValue}>${tooltip.uclVal.toFixed(2)}</span>
          </div>
          <div>
            <span style={styles.tooltipLabel}>LCL:</span>
            <span style={styles.tooltipValue}>${tooltip.lclVal.toFixed(2)}</span>
          </div>
          <div
            style={{
              ...styles.oocBadge,
              backgroundColor: tooltip.ooc ? '#fee2e2' : '#dcfce7',
              color: tooltip.ooc ? '#dc2626' : '#16a34a',
            }}
          >
            {tooltip.ooc ? 'OUT OF CONTROL' : 'In Control'}
          </div>
        </div>
      )}
    </div>
  );
}
